import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/hiring`;

const AMOUNT_FORMATS = [
  { value: 'actual', label: 'Actual ($)', divisor: 1 },
  { value: 'thousands', label: 'Thousands (K)', divisor: 1000 },
  { value: 'millions', label: 'Millions (M)', divisor: 1000000 },
];

const MonthlyCalculations = () => {
  const [calculations, setCalculations] = useState(null);
  const [year, setYear] = useState(2025);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [amountFormat, setAmountFormat] = useState('actual');

  useEffect(() => {
    fetchCalculations();
  }, [year, month]);

  const fetchCalculations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/monthly-calculations/${year}/${month}`);
      setCalculations(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching calculations:', error);
      setLoading(false);
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading calculations...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Period Selector */}
      <Card data-testid="period-selector-card">
        <CardHeader>
          <CardTitle>Monthly Cost Calculations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="space-y-2">
              <Label>Year</Label>
              <Select value={year.toString()} onValueChange={(val) => setYear(parseInt(val))}>
                <SelectTrigger className="w-32" data-testid="select-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2023, 2024, 2025, 2026, 2027].map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Month</Label>
              <Select value={month.toString()} onValueChange={(val) => setMonth(parseInt(val))}>
                <SelectTrigger className="w-40" data-testid="select-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthNames.map((name, idx) => (
                    <SelectItem key={idx + 1} value={(idx + 1).toString()}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Card */}
      {calculations && (
        <>
          <Card data-testid="cost-summary-card">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Cost Summary - {monthNames[month - 1]} {year}</CardTitle>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">Display:</Label>
                  <Select value={amountFormat} onValueChange={setAmountFormat}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AMOUNT_FORMATS.map((fmt) => (
                        <SelectItem key={fmt.value} value={fmt.value}>
                          {fmt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Base Salary</div>
                  <div className="text-2xl font-bold" data-testid="total-base-cost">
                    ${(calculations.total_base_cost / AMOUNT_FORMATS.find(f => f.value === amountFormat).divisor).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Benefits</div>
                  <div className="text-2xl font-bold" data-testid="total-benefits-cost">
                    ${(calculations.total_benefits_cost / AMOUNT_FORMATS.find(f => f.value === amountFormat).divisor).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Taxes</div>
                  <div className="text-2xl font-bold" data-testid="total-taxes-cost">
                    ${(calculations.total_taxes_cost / AMOUNT_FORMATS.find(f => f.value === amountFormat).divisor).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Pay Raises</div>
                  <div className="text-2xl font-bold" data-testid="total-raise-cost">
                    ${(calculations.total_raise_cost / AMOUNT_FORMATS.find(f => f.value === amountFormat).divisor).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Total Cost</div>
                  <div className="text-2xl font-bold text-green-600" data-testid="total-cost">
                    ${(calculations.total_cost / AMOUNT_FORMATS.find(f => f.value === amountFormat).divisor).toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-600 text-center mt-4">
                Values shown in {AMOUNT_FORMATS.find(f => f.value === amountFormat).label}
              </div>
            </CardContent>
          </Card>

          {/* Employee Details Table */}
          <Card data-testid="employee-details-card">
            <CardHeader>
              <CardTitle>Employee Cost Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead className="text-right">Base</TableHead>
                      <TableHead className="text-right">Benefits</TableHead>
                      <TableHead className="text-right">Taxes</TableHead>
                      <TableHead className="text-right">Raise</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Days Worked</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calculations.employee_details.map((emp) => {
                      const currentDivisor = AMOUNT_FORMATS.find(f => f.value === amountFormat).divisor;
                      return (
                        <TableRow key={emp.employee_id} data-testid={`cost-row-${emp.employee_id}`}>
                          <TableCell>{emp.employee_name}</TableCell>
                          <TableCell className="text-right">${(emp.base_cost / currentDivisor).toLocaleString()}</TableCell>
                          <TableCell className="text-right">${(emp.benefits_cost / currentDivisor).toLocaleString()}</TableCell>
                          <TableCell className="text-right">${(emp.taxes_cost / currentDivisor).toLocaleString()}</TableCell>
                          <TableCell className="text-right">${(emp.raise_cost / currentDivisor).toLocaleString()}</TableCell>
                          <TableCell className="text-right font-bold">${(emp.total_cost / currentDivisor).toLocaleString()}</TableCell>
                          <TableCell className="text-right">{emp.days_worked}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${
                              emp.is_prorated ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {emp.is_prorated ? 'Prorated' : 'Full Month'}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default MonthlyCalculations;